import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';

import { Card } from '../Card';
import { guideCardsImages, villaCardsImages } from '@/utils';
import { IHeaderDictionary } from '@/dictionaries/components/header/types';
import { IoIosArrowBack } from 'react-icons/io';
import useGlobalContext from '@/hooks/useGlobalContext';
import { cn } from '@/lib/utils';
import { VillasCount } from '@/services/types';

import SicilyFullMap from '@/assets/images/maps/sicily-full.png';
import PugliaFullMap from '@/assets/images/maps/puglia-full.png';

interface MenuContentProps {
  dict: IHeaderDictionary;
  lang: string;
  showVilla: boolean;
  showGuide: boolean;
  setShowVilla: Dispatch<SetStateAction<boolean>>;
  setShowGuide: Dispatch<SetStateAction<boolean>>;
  regionVillasCount: VillasCount[];
}

const MenuContent: FC<MenuContentProps> = ({
  dict,
  lang,
  showVilla,
  showGuide,
  setShowVilla,
  setShowGuide,
  regionVillasCount,
}) => {
  const [isCursorInsideDocument, setIsCursorInsideDocument] = useState(false);
  const { showingPromotionBanner } = useGlobalContext();

  const pathname = usePathname();

  let sicilyTotal = 0;
  let pugliaTotal = 0;

  if (regionVillasCount) {
    regionVillasCount.forEach((villa) => {
      if (villa.zone_code.startsWith('sicily')) {
        sicilyTotal += villa.number_of_villas;
      } else if (villa.zone_code.startsWith('puglia')) {
        pugliaTotal += villa.number_of_villas;
      }
    });
  }

  const customStyle =
    'min-w-[100%] sm:min-w-[250px] md:min-w-[300px] lg:min-w-[400px] desktop:min-w-[150px] max-w-[50%] before:h-[80%] before:bg-header-gradient hover:mb-2 hover:-mt-2 aspect-[6/4]';

  useEffect(() => {
    if (!isCursorInsideDocument) {
      if (showVilla) {
        setShowVilla(false);
      }
      if (showGuide) {
        setShowGuide(false);
      }
    }
    // eslint-disable-next-line
  }, [isCursorInsideDocument]);

  useEffect(() => {
    const handleMouseEnter = () => {
      setIsCursorInsideDocument(true);
    };

    const handleMouseLeave = () => {
      setIsCursorInsideDocument(false);
    };
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isCursorInsideDocument]);

  useEffect(() => {
    if (showVilla) {
      setShowVilla(false);
    }
    if (showGuide) {
      setShowGuide(false);
    }
    // eslint-disable-next-line
  }, [pathname]);

  return (
    <>
      <div
        className={cn(
          'fixed flex flex-wrap desktop:flex-nowrap justify-center w-full top-0 shadow-default z-[1000] transition-all duration-300 bg-white pb-8 pt-2.5 gap-2',
          {
            'opacity-100 -translate-y-0.5': showVilla,
            'opacity-0 -translate-y-2 invisible': !showVilla,
            'desktop:top-[163px]': showingPromotionBanner,
            'desktop:top-[100px]': !showingPromotionBanner,
          },
        )}
        onMouseLeave={() => setShowVilla(false)}>
        <div
          className="desktop:hidden flex items-center gap-1 w-full -ml-2 px-4 sm:px-10 desktop:px-20 cursor-pointer"
          onClick={() => setShowVilla(false)}>
          <IoIosArrowBack className="text-gray-500 w-6 h-6" />
          <span className="text-gray-700">{dict.mobileBack}</span>
        </div>
        <div
          className={`flex flex-wrap desktop:flex-nowrap justify-center w-full gap-4 sm:gap-8 max-w-default mx-auto px-4 sm:px-10 desktop:px-20`}>
          {dict.villaCards.map((card, index) => (
            <Card
              key={index}
              lang={lang}
              href={card.path}
              cardImage={villaCardsImages[index].banner}
              cardAlt={card.title}
              hoverContent={
                <p className="font-semibold uppercase">{dict.viewAll}</p>
              }
              customStyle={`${customStyle} desktop:max-w-[23%]`}
              hasBell={card.hasBell}
              sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw">
              <div className="relative flex">
                <div className="flex flex-col w-full">
                  <strong className="text-xl">{card.title}</strong>
                  <p>{`${card.id === 'sicily' ? sicilyTotal : pugliaTotal} ${
                    dict.nav.villa
                  }`}</p>
                </div>
                <div className="absolute right-0 -top-[15px] flex w-[95px] aspect-square overflow-hidden">
                  <Image
                    src={index === 0 ? SicilyFullMap : PugliaFullMap}
                    alt={card.title}
                    fill
                    className={
                      'group-hover:opacity-100 object-contain z-10 opacity-60 translate-y-[0.5px] absolute'
                    }
                    sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw"
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div
        className={cn(
          'fixed flex flex-wrap desktop:flex-nowrap justify-center w-full top-0 shadow-default z-[1000] transition-all duration-300 bg-white pb-8 pt-2.5 gap-2',
          {
            'opacity-100 -translate-y-0.5': showGuide,
            'opacity-0 -translate-y-2 invisible': !showGuide,
            'desktop:top-[163px]': showingPromotionBanner,
            'desktop:top-[100px]': !showingPromotionBanner,
          },
        )}
        onMouseLeave={() => setShowGuide(false)}>
        <div
          className="desktop:hidden flex items-center gap-1 w-full -ml-2 px-4 sm:px-10 desktop:px-20 cursor-pointer"
          onClick={() => setShowGuide(false)}>
          <IoIosArrowBack className="text-gray-500 w-6 h-6" />
          <span className="text-gray-700">{dict.mobileBack}</span>
        </div>
        <div
          className={`flex flex-wrap desktop:flex-nowrap justify-center w-full gap-4 sm:gap-8 max-w-default mx-auto px-4 sm:px-10 desktop:px-20`}>
          {dict.guideCards.map((card, index) => (
            <Card
              key={index}
              lang={lang}
              href={card.path}
              cardImage={guideCardsImages[index].banner}
              cardAlt={card.title}
              customStyle={`${customStyle} desktop:max-w-[23%]`}
              customContentStyle="absolute flex h-full p-4 z-10 w-full"
              sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw">
              <div className="flex w-full justify-center items-center">
                <strong className="text-xl">{card.title}</strong>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </>
  );
};

export default MenuContent;
